import { TFunction } from 'i18next';
import { FormikValues } from 'formik';

import {
  Resource,
  FormBlock,
  FormField,
  FormFieldType,
  FormSubmissionData,
  FormSubmissionFormData,
  CsvLookupReferenceValue,
  FormSubmissionPayloadData,
} from 'types';

import { isStaticFormField } from './static-form-field';
import { generateFormFieldsValidationSchema } from './validation';
import {
  isFormChoiceResponse,
  normalizeChoiceResponseValue,
} from './choice-response';

const toCsvLookupReference = (entry: unknown): CsvLookupReferenceValue => {
  if (entry && typeof entry === 'object') {
    if ('id' in entry && typeof entry.id === 'string') {
      return { referenceItemId: entry.id };
    }
    if (
      'referenceItemId' in entry &&
      typeof entry.referenceItemId === 'string'
    ) {
      return { referenceItemId: entry.referenceItemId };
    }
  }

  throw new TypeError('Invalid CSV lookup value');
};

export const getInitialValuesForFields = (
  fields: FormBlock['fields'],
  submissionData: FormSubmissionData | null
) => {
  let initialValues: FormikValues = {};

  fields.forEach((field) => {
    if (
      field.type === FormFieldType.PAGE_BREAK ||
      isStaticFormField(field.type)
    ) {
      return;
    }

    if (field.type === FormFieldType.SCALE_RATING) {
      initialValues[field.id] =
        submissionData?.[field.id] ??
        field.options?.options?.map(() => null) ??
        [];
    } else if (field.type === FormFieldType.FIELD_ARRAY) {
      if (field.options?.fields) {
        initialValues = {
          ...initialValues,
          ...getInitialValuesForFields(field.options?.fields, submissionData),
        };
      }
    } else if (
      field.type === FormFieldType.CHECKBOX ||
      (field.type === FormFieldType.SELECT && !!field.options?.multiple)
    ) {
      initialValues[field.id] = normalizeChoiceResponseValue(
        submissionData?.[field.id] ?? []
      );
    } else if (
      field.type === FormFieldType.SELECT ||
      field.type === FormFieldType.RADIO
    ) {
      initialValues[field.id] = normalizeChoiceResponseValue(
        submissionData?.[field.id] ?? null
      );
    } else if (field.type === FormFieldType.CSV_LOOKUP) {
      initialValues[field.id] = submissionData?.[field.id] ?? [];
    } else {
      initialValues[field.id] = submissionData?.[field.id] ?? '';
    }
  });
  return initialValues;
};

export const getInitialValuesForForm = (
  fields: FormField[],
  submissionData: FormSubmissionData | null
) => getInitialValuesForFields(fields, submissionData);

export const getDataForFormikForm = (
  fields: FormField[],
  submissionData: FormSubmissionData | null
) => {
  return {
    validationSchema: generateFormFieldsValidationSchema(fields),
    initialValues: getInitialValuesForForm(fields, submissionData),
  };
};

export const getResourceDuration = (resource: Resource): string => {
  const duration = resource.data.blocks.reduce(
    (total, block) => total + block.duration,
    0
  );
  const minutes = Math.ceil(duration / 60);

  return `${minutes} min${minutes !== 1 ? 's' : ''}`;
};

export const fixSubmissionTypes = (
  payload: FormSubmissionFormData,
  fields: FormField[]
): FormSubmissionPayloadData => {
  let newPayload = { ...payload };
  fields.forEach(({ id, type, options }) => {
    if (isStaticFormField(type)) {
      // Remove stale keys if an interactive field was changed to static content.
      delete newPayload[id];
      return;
    }
    const isFieldArray = type === FormFieldType.FIELD_ARRAY;
    if (
      newPayload[id] !== undefined &&
      newPayload[id] !== null &&
      !isFieldArray
    ) {
      if (type === FormFieldType.INPUT && options?.type === 'number') {
        newPayload[id] = Number(newPayload[id]);
      } else if (
        type === FormFieldType.CSV_LOOKUP &&
        Array.isArray(newPayload[id])
      ) {
        newPayload[id] = newPayload[id].map(toCsvLookupReference);
      }
    } else if (isFieldArray && options?.fields) {
      newPayload = fixSubmissionTypes(newPayload, options.fields);
    }
  });

  if (!isFormSubmissionPayloadData(newPayload)) {
    throw new TypeError('Invalid form submission payload');
  }

  return newPayload;
};

const isFormSubmissionPayloadData = (
  value: unknown
): value is FormSubmissionPayloadData => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

  return Object.values(value).every((entry) => {
    if (entry === null || isFormChoiceResponse(entry)) return true;
    if (typeof entry === 'string' || typeof entry === 'number') return true;
    if (Array.isArray(entry)) {
      return (
        entry.every(isFormChoiceResponse) ||
        entry.every((item) => typeof item === 'string') ||
        entry.every((item) => item === null || typeof item === 'number') ||
        entry.every(
          (item) =>
            Boolean(item) &&
            typeof item === 'object' &&
            'referenceItemId' in item &&
            typeof item.referenceItemId === 'string'
        )
      );
    }

    return isFormSubmissionPayloadData(entry);
  });
};

export const booleanOptions = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

export const getTranslatedBooleanOptions = (t: TFunction) => [
  { value: 'true', label: t('yes') },
  { label: t('no'), value: 'false' },
];

export const splitFieldsByPages = (fields: FormField[]) =>
  fields.reduce(
    (pages, field) => {
      if (field.type === FormFieldType.PAGE_BREAK) {
        // Start a new page
        pages.push([]);
      } else {
        // Add field to current page
        if (pages.length === 0) pages.push([]); // Ensure at least one page exists
        pages[pages.length - 1].push(field);
      }
      return pages;
    },
    [] as Array<Array<FormField>>
  );
